/**
 * Filter bar UI logic for PostIndexCollection.
 * Manages sorting/price filter icons and event listeners on Rails-rendered DOM.
 *
 * @param {object} vm - Vue component instance (Options API)
 * @returns {{ setupFilterBar: Function, updateFilterBarUI: Function, cleanupFilterListeners: Function }}
 */
export function useFilterBar(vm) {
  const trackedListeners = [];

  function addTrackedListener(target, event, handler) {
    target.addEventListener(event, handler);
    trackedListeners.push({ target, event, handler });
  }

  // --- Icon source detection ---

  let sortingCheckedSrc = null;
  let sortingUncheckedSrc = null;
  let priceCheckedSrc = null;
  let priceUncheckedSrc = null;

  function findIconSrcPair(selector, checkedKey, uncheckedKey) {
    let checkedSrc = null;
    let uncheckedSrc = null;
    document.querySelectorAll(selector).forEach((label) => {
      if (checkedSrc && uncheckedSrc) return;
      const img = label.querySelector("img");
      if (!img) return;
      const src = img.getAttribute("src");
      if (!checkedSrc && src.includes(checkedKey)) {
        checkedSrc = src;
      } else if (!uncheckedSrc && src.includes(uncheckedKey)) {
        uncheckedSrc = src;
      }
    });
    return { checkedSrc, uncheckedSrc };
  }

  function setIconState(img, isActive, checkedSrc, uncheckedSrc) {
    const src = isActive ? checkedSrc : uncheckedSrc;
    if (img && src) img.src = src;
  }

  function isSortingGroupHeader(label) {
    const parentDiv = label.closest(".my-2");
    return parentDiv ? !parentDiv.querySelector('input[type="radio"]') : false;
  }

  // --- UI update functions ---

  function updateSortingIcons() {
    const currentSort = vm.currentParams["o"];
    document.querySelectorAll(".post-sorting").forEach((label, idx) => {
      const img = label.querySelector("img");
      if (!img) return;
      const btnSort = label.dataset.postSorting;
      let isActive;
      if (!currentSort) {
        isActive = idx === 0;
      } else if (isSortingGroupHeader(label)) {
        isActive =
          currentSort === btnSort || currentSort.startsWith(btnSort + "_");
      } else {
        isActive = currentSort === btnSort;
      }
      setIconState(img, isActive, sortingCheckedSrc, sortingUncheckedSrc);
    });
  }

  function updatePriceIcons() {
    const currentMin = String(vm.currentParams["price_min"] ?? "");
    const currentMax = String(vm.currentParams["price_max"] ?? "");
    let hasMatch = false;
    const priceLabels = document.querySelectorAll(".price-filter");

    priceLabels.forEach((label) => {
      const img = label.querySelector("img");
      if (!img) return;
      const rangeNums = (label.dataset.priceFilter || "").match(/\d+/g);
      const isMatch =
        rangeNums && rangeNums[0] === currentMin && rangeNums[1] === currentMax;
      if (isMatch) hasMatch = true;
      setIconState(img, isMatch, priceCheckedSrc, priceUncheckedSrc);
    });

    if (!hasMatch && currentMin && currentMax && priceLabels.length > 0) {
      const lastImg = priceLabels[priceLabels.length - 1].querySelector("img");
      setIconState(lastImg, true, priceCheckedSrc, priceUncheckedSrc);
    }
  }

  function updateClearFilterButton() {
    const filterKeys = ["price_min", "price_max", "o", "c"];
    const hasActiveFilter = filterKeys.some(
      (k) => vm.currentParams[k] != null && vm.currentParams[k] !== "",
    );
    const clearBtn = document.getElementById("clear-filter-btn");
    if (clearBtn) clearBtn.classList.toggle("hidden", !hasActiveFilter);
  }

  function updateFilterBarUI() {
    updateSortingIcons();
    updatePriceIcons();
    updateClearFilterButton();
  }

  // --- Setup ---

  function setupFilterBar() {
    // Detect icon sources from existing DOM
    const sorting = findIconSrcPair(".post-sorting", "circle_check", "circle");
    sortingCheckedSrc = sorting.checkedSrc;
    sortingUncheckedSrc = sorting.uncheckedSrc;

    const price = findIconSrcPair(
      ".price-filter",
      "checkbox_checked",
      "checkbox_unchecked",
    );
    priceCheckedSrc = price.checkedSrc;
    priceUncheckedSrc = price.uncheckedSrc;

    // Sorting button listeners
    document.querySelectorAll(".post-sorting").forEach((button) => {
      addTrackedListener(button, "click", () => {
        vm.currentParams["o"] = button.dataset.postSorting;
        delete vm.currentParams["page"];
        updateFilterBarUI();
        vm.applyFilters();
      });
    });

    // Price filter button listeners
    document.querySelectorAll(".price-filter").forEach((button) => {
      addTrackedListener(button, "click", () => {
        const priceRange = button.dataset.priceFilter.match(/\d+/g).map(Number);
        vm.currentParams["price_min"] = priceRange[0];
        vm.currentParams["price_max"] = priceRange[1];
        delete vm.currentParams["page"];
        updateFilterBarUI();
        vm.applyFilters();
      });
    });

    // Price range manual inputs
    const minInput = document.querySelector(".price_min");
    const maxInput = document.querySelector(".price_max");
    if (minInput && maxInput) {
      minInput.value = vm.currentParams["price_min"] || 0;
      maxInput.value = vm.currentParams["price_max"] || 100;

      const priceBlurHandler = () => {
        vm.currentParams["price_min"] = minInput.value;
        vm.currentParams["price_max"] = maxInput.value;
        updateFilterBarUI();
        vm.applyFilters();
      };

      addTrackedListener(minInput, "blur", priceBlurHandler);
      addTrackedListener(maxInput, "blur", priceBlurHandler);
    }
  }

  function cleanupFilterListeners() {
    trackedListeners.forEach(({ target, event, handler }) => {
      target.removeEventListener(event, handler);
    });
    trackedListeners.length = 0;
  }

  return { setupFilterBar, updateFilterBarUI, cleanupFilterListeners };
}
